import { useMemo } from 'react';
import cn from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { Button, Checkbox, Column, Row, TextButton, Typography } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { useCobConfig } from "../../../CobConfigContext";
import { MobileSign } from "../../../components/MobileSign";
import { useResponsive } from "../../../../contexts";
import BankSecurityRow from "../../../../../components/BankSecurityRow/BankSecurityRow";
import { COBStep } from "../../../ClientOnboarding.utils";
import { getNextStepButtonText } from "../../ClientOnboardingV2.utils";
import { useSignAndApproveContext } from "../../hooks/useSignAndApprove";
import { GetPaymentMethodTID } from "../Steps/PaymentMethod/PaymentMethod";
import desktopStyles from "../Steps/StepsContainer/StepsContainerDesktop.module.scss";
import styles from "./Footer.module.scss";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const ANCHOR_TOS_URL = 'https://www.sayanchor.com/terms-of-contact';
const collapseVariants = {
  hidden: {
    height: 0,
    opacity: 0,
    marginBottom: 0
  },
  visible: {
    height: 'auto',
    opacity: 1,
    marginBottom: 12
  }
};
const collapseTransition = {
  duration: 0.3,
  ease: 'easeInOut'
};
export const GetFooterTID = parentTestId => getTestIds({
  parentTestId
});
export const Footer = _ref => {
  let {
    currentStep,
    relationship,
    onNext,
    testId,
    isNextDisabled,
    hideNextButton,
    statement,
    isStatementAccepted,
    onStatementChange
  } = _ref;
  const {
    isMobile
  } = useResponsive();
  const {
    FooterTestIds
  } = useMemo(() => GetFooterTID(testId), [testId]);
  const {
    PaymentMethodTestIds
  } = useMemo(() => GetPaymentMethodTID(), []);
  const nextStepButtonText = getNextStepButtonText(currentStep, relationship);
  const showCheckbox = statement && onStatementChange;
  const isSignAndApproveStep = currentStep === COBStep.signAndApprove;
  const {
    signature
  } = useSignAndApproveContext();
  const {
    isPreview
  } = useCobConfig();
  const hasSignature = !!signature;
  const showTermsText = hasSignature && !isPreview;
  if (hideNextButton) {
    return /*#__PURE__*/_jsx("div", {
      className: isMobile ? styles.mobileFooterSpacer : styles.footerSpacer
    });
  }
  if (isMobile) {
    const showBankSecurityRow = currentStep === COBStep.paymentMethod;
    return /*#__PURE__*/_jsx(motion.div, {
      className: styles.mobileFooter,
      "data-testid": FooterTestIds.Root(),
      layout: true,
      transition: collapseTransition,
      children: /*#__PURE__*/_jsxs(Column, {
        className: cn(styles.mobileFooterContent, isSignAndApproveStep && styles.mobileFooterContentCentered),
        gap: isSignAndApproveStep ? 0 : 16,
        children: [isSignAndApproveStep && /*#__PURE__*/_jsx(Typography, {
          variant: "paragraph3",
          skin: "secondary",
          weight: "light",
          className: cn(styles.signAndApproveHint, styles.signAndApproveHintCentered),
          children: showTermsText ? /*#__PURE__*/_jsxs(_Fragment, {
            children: ["By approving this agreement, you also agree to Anchor's", ' ', /*#__PURE__*/_jsx(TextButton, {
              as: "a",
              href: ANCHOR_TOS_URL,
              target: "_blank",
              rel: "noopener noreferrer",
              variant: "paragraph3",
              children: "Terms of Service"
            }), "."]
          }) : 'Sign above to approve the agreement'
        }), /*#__PURE__*/_jsx(AnimatePresence, {
          initial: false,
          children: showCheckbox && /*#__PURE__*/_jsx(motion.div, {
            variants: collapseVariants,
            initial: "hidden",
            animate: "visible",
            exit: "hidden",
            transition: collapseTransition,
            style: {
              overflow: 'hidden'
            },
            children: /*#__PURE__*/_jsx(Checkbox, {
              className: styles.statementCheckbox,
              onChange: e => onStatementChange(e.target.checked),
              testId: FooterTestIds.StatementCheckbox(),
              checked: isStatementAccepted,
              children: /*#__PURE__*/_jsx(Typography, {
                variant: "paragraph3",
                weight: "light",
                children: statement
              })
            })
          }, "checkbox-collapse")
        }), showBankSecurityRow && /*#__PURE__*/_jsx(BankSecurityRow, {
          mobileView: true,
          className: styles.bankSecurityRow,
          "data-testid": PaymentMethodTestIds.BankSecurityRow()
        }), isSignAndApproveStep ? /*#__PURE__*/_jsx("div", {
          className: styles.mobileSignWrapper,
          "data-testid": FooterTestIds.NextButton(),
          children: /*#__PURE__*/_jsx(MobileSign, {
            relationship: relationship,
            disabled: isNextDisabled,
            onSign: onNext,
            hideSignatoryTypography: true
          })
        }) : /*#__PURE__*/_jsx(Button, {
          onClick: onNext,
          className: styles.mobileFooterButton,
          testId: FooterTestIds.NextButton(),
          disabled: isNextDisabled,
          children: nextStepButtonText
        })]
      })
    });
  }
  const showSignAndApproveFooter = isSignAndApproveStep;
  return /*#__PURE__*/_jsx(Row, {
    className: cn(desktopStyles.footer, {
      [styles.footerWithCheckbox]: showCheckbox,
      [styles.footerWithSignAndApprove]: showSignAndApproveFooter
    }),
    testId: FooterTestIds.Root(),
    children: /*#__PURE__*/_jsxs(Row, {
      className: cn(styles.footerContent, {
        [styles.footerContentWithCheckbox]: showCheckbox,
        [styles.footerContentWithSignAndApprove]: showSignAndApproveFooter
      }),
      testId: FooterTestIds.FooterContent(),
      children: [showCheckbox && /*#__PURE__*/_jsx(Row, {
        children: /*#__PURE__*/_jsx(Checkbox, {
          className: styles.statementCheckbox,
          onChange: e => onStatementChange(e.target.checked),
          testId: FooterTestIds.StatementCheckbox(),
          checked: isStatementAccepted,
          children: /*#__PURE__*/_jsx(Typography, {
            variant: "paragraph2",
            weight: "light",
            children: statement
          })
        })
      }), showSignAndApproveFooter && /*#__PURE__*/_jsx(Typography, {
        variant: "paragraph2",
        skin: "secondary",
        weight: "light",
        className: styles.signAndApproveHint,
        children: showTermsText ? /*#__PURE__*/_jsxs(_Fragment, {
          children: ["By approving this agreement, you also agree to Anchor's", ' ', /*#__PURE__*/_jsx(TextButton, {
            as: "a",
            href: ANCHOR_TOS_URL,
            target: "_blank",
            rel: "noopener noreferrer",
            variant: "paragraph2",
            children: "Terms of Service"
          }), "."]
        }) : 'Sign above to approve the agreement'
      }), /*#__PURE__*/_jsx(Button, {
        onClick: onNext,
        className: desktopStyles.button,
        testId: FooterTestIds.NextButton(),
        disabled: isNextDisabled,
        children: nextStepButtonText
      })]
    })
  });
};